import { shouldPolyfill } from '@formatjs/intl-segmenter/should-polyfill';
import { getFramework } from '@/framework';
import { Layer } from '@/layers/layer';
import { type IServerTextLayerProps } from '@/version-parse';
import {
    IListStyleMaterial,
    TextAlignType,
    TextShapeType,
    TextWritingType,
    type EditingRange,
    type IAdvancedTextMaterial,
    type ILayerProps,
    type IShapeExtension,
    type ITextLayerProps,
    type ITextMark,
    type ITexture,
    type ListStyleStruct,
} from '@/version-types';
import {
    type BackgroundAdjustPayload,
    type GlowAdjustPayload,
    type ShadowAdjustPayload,
    type StrokeAdjustPayload,
    type TextEffectKind,
    type TextEffectPreset,
} from '../types';
import { PureTextKind } from '../utils';
import { Alignment, type IAlignment } from './alignment';
import { ArtText, type ArtTextCharacterConfig, type IArtText } from './art-text';
import { FillColor, type IFillColor } from './fill-color';
import { FontFamily, type IFontFamily } from './font-family';
import { FontSize, type IFontSize } from './font-size';
import { FontStyle, type IFontStyle } from './font-style';
import { FormatBrush, type IFormatBrush } from './format-brush';
import { ListText, type IListText } from './list-text';
import { TextMaterial, type ITextMaterial } from './material';
import { Opacity, type IOpacity } from './opacity';
import { Replacer, type IReplacer, type SpecificTextStyle } from './replacer';
import { ShapeText, ShapeTextPropsOptions, type IShapeText, type UnsetOptions } from './shape-text';
import { Shortcut, type IShortcut } from './shortcut';
import { Spacing, type ISpacing } from './spacing';
import { TextDecoration, type ITextDecoration } from './text-decoration';
import { TextEffect, type ITextEffect } from './text-effect';
import { TextMarkup, type ITextMarkup } from './text-mark';
import { WritingMode, type IWritingMode } from './writing-mode';

interface ITextLayer
    extends IShortcut,
        ITextMaterial,
        IShapeText,
        IFontFamily,
        IFontSize,
        IFontStyle,
        ITextDecoration,
        IWritingMode,
        ITextEffect,
        IListText,
        IAlignment,
        ISpacing,
        ITextMarkup,
        IFormatBrush,
        IReplacer,
        IOpacity,
        IArtText {}

export class TextLayer extends Layer implements ITextLayer {
    private shortcut: IShortcut;
    private textMaterial: ITextMaterial;
    private shapeText: IShapeText;
    private fontFamily: IFontFamily;
    private fontSize: IFontSize;
    private fontStyle: IFontStyle;
    private fillColor: IFillColor;
    private listText: IListText;
    private alignment: IAlignment;
    private spacing: ISpacing;
    private textDecoration: ITextDecoration;
    private writingMode: IWritingMode;
    private textEffect: ITextEffect;
    private textMarkup: ITextMarkup;
    private formatBrush: IFormatBrush;
    private replacer: IReplacer;
    private opacity: IOpacity;
    private artText: IArtText;

    constructor(identity: string) {
        super(identity);

        const framework = getFramework(identity);

        this.shortcut = new Shortcut(framework, this);
        this.textMaterial = new TextMaterial(framework, this);
        this.shapeText = new ShapeText(framework);
        this.fontFamily = new FontFamily(framework, this.shapeText);
        this.fontSize = new FontSize(framework, this.shapeText);
        this.fontStyle = new FontStyle(framework, this.shapeText);
        this.fillColor = new FillColor(framework, this.shapeText);
        this.textDecoration = new TextDecoration(framework, this.shapeText);
        this.writingMode = new WritingMode(framework, this.shapeText);
        this.listText = new ListText(framework);
        this.alignment = new Alignment(framework);
        this.spacing = new Spacing(framework, this.shapeText);
        this.textEffect = new TextEffect(framework, this.shapeText);
        this.textMarkup = new TextMarkup(framework);
        this.replacer = new Replacer(framework, this.shapeText);
        this.opacity = new Opacity(framework);
        this.artText = new ArtText(framework);
        this.formatBrush = new FormatBrush(framework, this.shapeText, this.artText);
    }

    createPureTextLayer(kind: PureTextKind, stageIndex?: number) {
        return this.shortcut.createPureTextLayer(kind, stageIndex);
    }

    //#region shortcut
    addByDefaultTitle(byMousePosition?: boolean) {
        return this.shortcut.addByDefaultTitle(byMousePosition);
    }

    addByDefaultSubTitle(byMousePosition?: boolean) {
        return this.shortcut.addByDefaultSubTitle(byMousePosition);
    }

    addByDefaultContent(byMousePosition?: boolean) {
        return this.shortcut.addByDefaultContent(byMousePosition);
    }

    addByDefaultShapeText(materialID: string) {
        return this.shortcut.addByDefaultShapeText(materialID);
    }

    addByDefaultEffectText(byMousePosition?: boolean) {
        return this.shortcut.addByDefaultEffectText(byMousePosition);
    }

    addByCustomLayerData(layerData: Partial<ITextLayerProps>, options = { byStraight: false, byMousePosition: false }) {
        return this.shortcut.addByCustomLayerData(layerData, options);
    }
    //#endregion

    //#region material
    storeMaterialLayerToCache(materialID: string, serveLayer: IServerTextLayerProps, version?: string) {
        return this.textMaterial.storeMaterialLayerToCache(materialID, serveLayer, version);
    }

    loadMaterialLayerFromCache(materialID: string) {
        return this.textMaterial.loadMaterialLayerFromCache(materialID);
    }

    addByMaterialLayer(layer: ILayerProps, byMousePosition?: boolean) {
        return this.textMaterial.addByMaterialLayer(layer, byMousePosition);
    }
    // #endregion

    // #region shape-text
    loadWasmAssets() {
        return this.shapeText.loadWasmAssets();
    }

    applyShapeText(
        layerID: string,
        shapeType: TextShapeType,
        options?: { materialID?: number; params?: IShapeExtension },
    ) {
        return this.shapeText.applyShapeText(layerID, shapeType, options);
    }

    cancelShapeEffect(layerID: string, options?: UnsetOptions) {
        return this.shapeText.cancelShapeEffect(layerID, options);
    }

    adjustShapeTextProps(layerID: string, key: string, value: number | boolean, options: ShapeTextPropsOptions) {
        return this.shapeText.adjustShapeTextProps(layerID, key, value, options);
    }

    refreshLayerScreenshotURL(layer: ILayerProps) {
        return this.shapeText.refreshLayerScreenshotURL(layer);
    }

    onCancelShapeText(fn: (layer: ILayerProps) => void) {
        return this.shapeText.onCancelShapeText(fn);
    }
    // #endregion

    // #region font-base
    __setFontFamily(layerID: string, fontFamily: string, options?: { range?: EditingRange | null }) {
        return this.fontFamily.__setFontFamily(layerID, fontFamily, options);
    }

    __setFontSize(
        layerID: string,
        fontSize: number,
        options?: { range?: EditingRange | null; writeHistory?: boolean; clearPrevLayer?: boolean },
    ) {
        return this.fontSize.__setFontSize(layerID, fontSize, options);
    }

    __setItalicStyle(
        layerID: string,
        flag: boolean,
        options?: { range?: EditingRange | null; writeHistory?: boolean },
    ) {
        return this.fontStyle.__setItalicStyle(layerID, flag, options);
    }

    __setBoldStyle(layerID: string, flag: boolean, options?: { range?: EditingRange | null; writeHistory?: boolean }) {
        return this.fontStyle.__setBoldStyle(layerID, flag, options);
    }
    // #endregion

    // #region text-color
    __setFillColor(
        layerID: string,
        color: string,
        options?: { range?: EditingRange | null; originLayer?: ILayerProps },
    ) {
        return this.fillColor.__setFillColor(layerID, color, options);
    }

    setTexture(layerID: string, texture: ITexture) {
        return this.fillColor.setTexture(layerID, texture);
    }

    setFillOpacity(layerID: string, opacity: number, options?: { originLayer?: ILayerProps }) {
        return this.fillColor.setFillOpacity(layerID, opacity, options);
    }
    // #endregion

    // #region list-text
    applyListText(layerID: string, data: ListStyleStruct) {
        return this.listText.applyListText(layerID, data);
    }

    cancelListText(layerID: string) {
        return this.listText.cancelListText(layerID);
    }

    updateListStyleMaterial(
        layerID: string,
        data: IListStyleMaterial,
        options?: { writeHistory?: boolean },
    ): ILayerProps | void {
        return this.listText.updateListStyleMaterial(layerID, data, options);
    }
    // #endregion

    // #region text-decoration
    __setUnderline(layerID: string, flag: boolean, options?: { range?: EditingRange | null; writeHistory?: boolean }) {
        return this.textDecoration.__setUnderline(layerID, flag, options);
    }

    __setLineThrough(
        layerID: string,
        flag: boolean,
        options?: { range?: EditingRange | null; writeHistory?: boolean },
    ) {
        return this.textDecoration.__setLineThrough(layerID, flag, options);
    }
    // #endregion

    // #region writing-mode
    setWritingMode(layerID: string, writingMode: TextWritingType) {
        return this.writingMode.setWritingMode(layerID, writingMode);
    }
    // #endregion

    // #region alignment
    setTextAlign(layerID: string, align: TextAlignType) {
        return this.alignment.setTextAlign(layerID, align);
    }
    // #endregion

    // #region spacing
    setLineSpacing(layerID: string, value: number, options?: { writeHistory: boolean; clearPrevLayer?: boolean }) {
        return this.spacing.setLineSpacing(layerID, value, options);
    }

    setLetterSpacing(layerID: string, value: number, options?: { writeHistory: boolean; clearPrevLayer?: boolean }) {
        return this.spacing.setLetterSpacing(layerID, value, options);
    }
    // #endregion

    // #region text-effect
    addStroke(layerID: string, options?: { strokeColor?: string }) {
        return this.textEffect.addStroke(layerID, options);
    }

    addShadow(layerID: string, options?: { shadowColor?: string }) {
        return this.textEffect.addShadow(layerID, options);
    }

    addGlow(layerID: string, options?: { glowColor?: string }) {
        return this.textEffect.addGlow(layerID, options);
    }

    addBackground(layerID: string, options?: { backgroundColor?: string }) {
        return this.textEffect.addBackground(layerID, options);
    }

    setEffectVisibility(layerID: string, effect: TextEffectKind, flag: boolean, index?: number) {
        return this.textEffect.setEffectVisibility(layerID, effect, flag, index);
    }

    removeEffect(layerID: string, effect: TextEffectKind, index?: number) {
        return this.textEffect.removeEffect(layerID, effect, index);
    }

    clearAllEffects(layerID: string) {
        return this.textEffect.clearAllEffects(layerID);
    }

    applyEffectPreset(layerID: string, preset: TextEffectPreset) {
        return this.textEffect.applyEffectPreset(layerID, preset);
    }

    adjustStrokeProps(
        layerID: string,
        payload: StrokeAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ) {
        return this.textEffect.adjustStrokeProps(layerID, payload, options);
    }

    adjustShadowProps(
        layerID: string,
        payload: ShadowAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ) {
        return this.textEffect.adjustShadowProps(layerID, payload, options);
    }

    adjustGlowProps(
        layerID: string,
        payload: GlowAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ) {
        return this.textEffect.adjustGlowProps(layerID, payload, options);
    }

    adjustBackgroundProps(
        layerID: string,
        payload: BackgroundAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ) {
        return this.textEffect.adjustBackgroundProps(layerID, payload, options);
    }
    // #endregion

    // #region text-markup
    __setTextMark(layerID: string, value: ITextMark, options?: { range?: EditingRange | null }) {
        return this.textMarkup.__setTextMark(layerID, value, options);
    }

    __cancelTextMark(layerID: string, options?: { range?: EditingRange | null }) {
        return this.textMarkup.__cancelTextMark(layerID, options);
    }

    __updateTextMark(
        layerID: string,
        value: ITextMark,
        options?: { range?: EditingRange | null; originLayer?: ILayerProps },
    ) {
        return this.textMarkup.__updateTextMark(layerID, value, options);
    }
    // #endregion

    // #region format-brush
    extractFormatBrushData(layerID: string) {
        return this.formatBrush.extractFormatBrushData(layerID);
    }

    applyFormatBrush(layerID: string) {
        return this.formatBrush.applyFormatBrush(layerID);
    }
    // #endregion

    // #region replace
    replaceLayers(prevLayers: ILayerProps[], nextLayers: ILayerProps[], historyFlag?: string) {
        return this.replacer.replaceLayers(prevLayers, nextLayers, historyFlag);
    }

    replaceLayerText(
        layer: ILayerProps,
        text: string,
        options?: { resetLayerID?: boolean; update?: boolean; triggerFuncID?: string; keepConstantWidth?: boolean },
    ) {
        return this.replacer.replaceLayerText(layer, text, options);
    }

    updateBySpecificStyle(layerID: string, style: SpecificTextStyle, fontMaterialID?: number) {
        return this.replacer.updateBySpecificStyle(layerID, style, fontMaterialID);
    }
    // #endregion

    // #region opacity
    setLayerOpacity(layerID: string, opacity: number, options?: { writeHistory?: boolean }) {
        return this.opacity.setLayerOpacity(layerID, opacity, options);
    }
    // #endregion

    // #region art-text
    applyArtText(layerID: string, data: IAdvancedTextMaterial, options = { writeHistory: true }) {
        return this.artText.applyArtText(layerID, data, options);
    }

    cancelArtText(layerID: string) {
        return this.artText.cancelArtText(layerID);
    }

    __adjustArtTextSingleCharacter(
        layerID: string,
        partialConfig: ArtTextCharacterConfig,
        options: { range: EditingRange | null; writeHistory?: boolean },
    ): ILayerProps | void {
        return this.artText.__adjustArtTextSingleCharacter(layerID, partialConfig, options);
    }
    // #endregion

    /**
     * polyfill Intl.Segmenter
     */
    async polyfill() {
        if (shouldPolyfill()) {
            // eslint-disable-next-line @cspell/spellchecker
            await import('@formatjs/intl-segmenter/polyfill-force');
        }
    }
}
